import { useState, useCallback, useMemo, useEffect } from "react";
import { IStatelessFormService, StatelessFormService } from "../../entities/stateless-form-service";
import { BwjInput } from "../../framework/entities/bwj-input";
import { BwjValidityState } from "../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../framework/services/event-notification";
import { ValidatedFormValueService, IFormValidityCheck } from "../../framework/services/form-model-service-components";
import { NavigationService } from "../../framework/services/navigation";
import { contentChanged } from "../../framework/utils";
import { none, where } from "../../framework/utils/collection";
import { ICustomerEnrollmentFormModel, ICustomerEnrollmentValueModel, ICustomerEnrollmentModelChange } from "./customer-enrollment-iface";
import { isMeaningfulString } from "../../framework/utils/string";
import { ICreateProjectRequest } from "../../entities-generated/models/project/create-project-request";

// ##BEGIN:DEVELOPERCODE##

export const CustomerEnrollmentModelDidUpdate = 'BrowseAppEvents.CustomerEnrollmentModelDidUpdate';

// ##END:DEVELOPERCODE##

// %%BEGIN:name/validated/string/string%%
export const name = new (class nameService extends ValidatedFormValueService<IStatelessFormService, string, string> {
    public initialize(data: IStatelessFormService): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStatelessFormService): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        value.length > 60 && (value = value.substring(0, 60));
        const result = this.requiredFieldValidator(value, 'Please enter your name');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'name' // id
    ,'Your name' // description
);
// %%END:name/validated/string/string%%

// %%BEGIN:zipCode/validated/string/string%%
export const zipCode = new (class zipCodeService extends ValidatedFormValueService<IStatelessFormService, string, string> {
    public initialize(data: IStatelessFormService): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStatelessFormService): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Please enter your zip code');
        if(result.validity === BwjValidityState.Valid
            && (/[0-9]/g.test(value) === false || /^\d{5}$/.test(value) === false)
        ) {
            result.validity = BwjValidityState.Invalid;
            result.message = 'Please enter a valid zip code';
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'zipCode' // id
    ,'Zip Code' // description
);
// %%END:zipCode/validated/string/string%%

// %%BEGIN:email/validated/string/string%%
export const email = new (class emailService extends ValidatedFormValueService<IStatelessFormService, string, string> {
    public initialize(data: IStatelessFormService): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStatelessFormService): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        value.length > 200 && (value = value.substring(0, 200));
        const result = this.requiredFieldValidator(value, 'Please provide your email address');
        if(result.validity === BwjValidityState.Valid
            && /^[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(value) === false
        ) {
            result.validity = BwjValidityState.Invalid;
            result.message = 'Please make sure your email is spelled correctly';
        }
        
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'email' // id
    ,'Email address' // description
);
// %%END:email/validated/string/string%%

// %%BEGIN:phone/validated/string/string%%
export const phone = new (class phoneService extends ValidatedFormValueService<IStatelessFormService, string, string> {
    public initialize(data: IStatelessFormService): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IStatelessFormService): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value.replaceAll(/[^0-9]/g, '');
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        if(isMeaningfulString(value)) {
            const ipt = value.replaceAll(/[^0-9]/g, '');
            if(/[0-9]/g.test(ipt) === false) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Please enter a valid number';
            } else if(ipt.length === 7) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Please include your area code';
            } else if(/^[2-9]\d{9}$/.test(ipt) === false) {
                result.validity = BwjValidityState.Invalid;
                result.message = 'Please make sure your phone number is correct';
            }
        }
        
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }
})(
    'phone' // id
    ,'Phone number' // description
);
// %%END:phone/validated/string/string%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): ICustomerEnrollmentFormModel => {
    return {
       name: name.getInputValue(),
       zipCode: zipCode.getInputValue(),
       email: email.getInputValue(),
       phone: phone.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): ICustomerEnrollmentValueModel => {
    return {
       name: name.getOutputValue(),
       zipCode: zipCode.getOutputValue(),
       email: email.getOutputValue(),
       phone: phone.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        name.getInputValue(),
        zipCode.getInputValue(),
        email.getInputValue(),
        phone.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: ICustomerEnrollmentModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(CustomerEnrollmentModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<ICustomerEnrollmentFormModel>> => {
    const promises = [
        name.formValidateAndUpdate(),
        zipCode.formValidateAndUpdate(),
        email.formValidateAndUpdate(),
        phone.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:ICustomerEnrollmentFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: ICustomerEnrollmentValueModel, dataModel: IStatelessFormService } = {
    dataModel: new StatelessFormService()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IStatelessFormService) => {
    const dataModel = data || new StatelessFormService();

    name.initialize(dataModel);
    zipCode.initialize(dataModel);
    email.initialize(dataModel);
    phone.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:ICustomerEnrollmentFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
name.setChangePublisher(broadcastChange);
zipCode.setChangePublisher(broadcastChange);
email.setChangePublisher(broadcastChange);
phone.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new StatelessFormService();

    name.applyToDataModel(dataModel);
    zipCode.applyToDataModel(dataModel);
    email.applyToDataModel(dataModel);
    phone.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: ICustomerEnrollmentModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], CustomerEnrollmentModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): ICustomerEnrollmentFormModel => {
    const [getter, setter] = useState<ICustomerEnrollmentFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: ICustomerEnrollmentModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: ICustomerEnrollmentFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('CustomerEnrollmentFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('CustomerEnrollmentFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toCreateProjectRequest = () => {
    const v = toValueModel();
    const request: ICreateProjectRequest = {
        name: v.name,
        zipCode: v.zipCode,
        email: v.email,
        phone: v.phone
    };
    return request;
};

// ##END:DEVELOPERCODE##
